import * as Yup from 'yup';
import { COUNTRY_ISO_CODES } from 'checkout/enums/CountryIsoCodes';
import { t } from 'localization/utils/t';
import { getTranslations } from 'shared/utils/getTranslations';

const TP = 'shared.utils.validations';

export const ErrorMessage = {
  existingEmail: {
    value: t(`${TP}.existingEmail`, 'Email has already been taken'),
    TK: 'existingEmail',
    TP,
  },
  incompleteFullName: {
    value: t(`${TP}.incompleteFullName`, 'Name must contain first and last name.'),
    TK: 'incompleteFullName',
    TP,
  },
  invalidChineseName: {
    value: t(`${TP}.invalidChineseName`, 'Name should be in Chinese.'),
    TK: 'invalidChineseName',
    TP,
  },
  invalidCnid: {
    value: t(`${TP}.invalidCnid`, 'Enter a valid Chinese Id Card Number.'),
    TK: 'invalidCnid',
    TP,
  },
  invalidEmail: {
    value: t(`${TP}.invalidEmail`, 'Enter a valid email address'),
    TK: 'invalidEmail',
    TP,
  },
  invalidFullName: {
    value: t(`${TP}.invalidFullName`, 'Name cannot contain numbers and special characters.'),
    TK: 'invalidFullName',
    TP,
  },
  invalidOTPCode: {
    value: t(`${TP}.invalidOTPCode`, 'Code must be numerical'),
    TK: 'invalidOTPCode',
    TP,
  },
  invalidUsPhone: {
    value: t(`${TP}.invalidUsPhone`, 'Phone number is not a valid US number.'),
    TK: 'invalidUsPhone',
    TP,
  },
  invalidPhone: {
    value: t(`${TP}.invalidPhone`, 'Phone number is not valid.'),
    TK: 'invalidPhone',
    TP,
  },
  invalidUsername: {
    value: t(`${TP}.invalidUsername`, 'Username only allows letters, numbers and _'),
    TK: 'invalidUsername',
    TP,
  },
  // this one shows up when zipcode is malformatted or does not match the state
  invalidStateCodeZip: {
    value: t(
      `${TP}.invalidStateCodeZip`,
      'The State code in the address is invalid. Note that only US State and U.S. Territories and possession abbreviations are valid.',
    ),
    TK: 'invalidStateCodeZip',
    TP,
  },
  invalidZip: {
    value: t(
      `${TP}.invalidZip`,
      'ZIP Code could not be found because neither a valid City, State, nor valid 5- digit ZIP Code was present.',
    ),
    TK: 'invalidZip',
    TP,
  },
  requiredAddress: {
    value: t(`${TP}.requiredAddress`, 'Street address is a required field.'),
    TK: 'requiredAddress',
    TP,
  },
  requiredCity: {
    value: t(`${TP}.requiredCity`, 'City is a required field.'),
    TK: 'requiredCity',
    TP,
  },
  requiredCnid: {
    value: t(`${TP}.requiredCnid`, 'Chinese Id Card Number is a required field.'),
    TK: 'requiredCnid',
    TP,
  },
  requiredCountry: {
    value: t(`${TP}.requiredCountry`, 'Country or region is a required field.'),
    TK: 'requiredCountry',
    TP,
  },
  requiredDistrict: {
    value: t(`${TP}.requiredDistrict`, 'District is a required field'),
    TK: 'requiredDistrict',
    TP,
  },
  requiredEmail: {
    value: t(`${TP}.requiredEmail`, 'Email address is a required field.'),
    TK: 'requiredEmail',
    TP,
  },
  requiredEmailOrUsername: {
    value: t(`${TP}.requiredEmailOrUsername`, 'Email or Username is a required field.'),
    TK: 'requiredEmailOrUsername',
    TP,
  },
  requiredFullName: {
    value: t(`${TP}.requiredFullName`, 'Full name is a required field.'),
    TK: 'requiredFullName',
    TP,
  },
  requiredOTPCode: {
    value: t(`${TP}.requiredOTPCode`, 'Code is a required field.'),
    TK: 'requiredOTPCode',
    TP,
  },
  requiredPassword: {
    value: t(`${TP}.requiredPassword`, 'Password is a required field.'),
    TK: 'requiredPassword',
    TP,
  },
  requiredPhone: {
    value: t(`${TP}.requiredPhone`, 'Phone is a required field.'),
    TK: 'requiredPhone',
    TP,
  },
  requiredPostalCode: {
    value: t(`${TP}.requiredPostalCode`, 'Zip or postal code is a required field.'),
    TK: 'requiredPostalCode',
    TP,
  },
  requiredState: {
    value: t(`${TP}.requiredState`, 'State / Province is a required field'),
    TK: 'requiredState',
    TP,
  },
  requiredStateAU: {
    value: t(`${TP}.requiredStateAU`, 'State is a required field'),
    TK: 'requiredStateAU',
    TP,
  },
  requiredStateCA: {
    value: t(`${TP}.requiredStateCA`, 'Province or Territory is a required field'),
    TK: 'requiredStateCA',
    TP,
  },
  requiredStateUK: {
    value: t(`${TP}.requiredStateUK`, 'County is a required field'),
    TK: 'requiredStateUK',
    TP,
  },
  requiredStateUS: {
    value: t(`${TP}.requiredStateUS`, 'State is a required field'),
    TK: 'requiredStateUS',
    TP,
  },
  shortUsername: {
    value: t(`${TP}.shortUsername`, 'Username is too short (minimum is 4 characters)'),
    TK: 'shortUsername',
    TP,
  },
};

const CHINESE_NAME_PATTERN =
  /^([\u3400-\u4DB5\u4E00-\u9FCC\uFA0E\uFA0F\uFA11\uFA13\uFA14\uFA1F\uFA21\uFA23\uFA24\uFA27-\uFA29]|[\uD840-\uD868][\uDC00-\uDFFF]|\uD869[\uDC00-\uDED6\uDF00-\uDFFF]|[\uD86A-\uD86C][\uDC00-\uDFFF]|\uD86D[\uDC00-\uDF34\uDF40-\uDFFF]|\uD86E[\uDC00-\uDC1D]| )*$/;

export const sharedValidationsMap = {
  addressErrorFieldMap: () => {
    const [
      incompleteFullName,
      invalidChineseName,
      invalidCnid,
      invalidFullName,
      invalidZip,
      invalidUsPhone,
      existingEmail,
      invalidStateCodeZip,
    ] = getTranslations([
      ErrorMessage.incompleteFullName,
      ErrorMessage.invalidChineseName,
      ErrorMessage.invalidCnid,
      ErrorMessage.invalidFullName,
      ErrorMessage.invalidZip,
      ErrorMessage.invalidUsPhone,
      ErrorMessage.existingEmail,
      ErrorMessage.invalidStateCodeZip,
    ]);
    return new Map<string, string>([
      [incompleteFullName, 'fullName'],
      [invalidChineseName, 'fullName'],
      [invalidCnid, 'chineseIdCardNumber'],
      [invalidFullName, 'fullName'],
      [invalidZip, 'postalCode'],
      [invalidUsPhone, 'phone'],
      [existingEmail, 'email'],
      [invalidStateCodeZip, 'postalCode'],
    ]);
  },
  emailValidation: () => {
    const invalidMessage = getTranslations(ErrorMessage.invalidEmail);
    return Yup.string().email(invalidMessage);
  },
  requiredEmailValidation: () => {
    const [requiredEmailErrorMessage, invalidEmailErrorMessage] = getTranslations([
      ErrorMessage.requiredEmail,
      ErrorMessage.invalidEmail,
    ]);
    return Yup.string().email(invalidEmailErrorMessage).required(requiredEmailErrorMessage);
  },
  emailOrUsernameValidation: () => {
    const invalidMessage = getTranslations(ErrorMessage.requiredEmailOrUsername);
    return Yup.string().required(invalidMessage);
  },
  chineseCnidValidation: () => {
    const [invalidCnid, requiredCnid] = getTranslations([
      ErrorMessage.invalidCnid,
      ErrorMessage.requiredCnid,
    ]);
    return Yup.string()
      .matches(/^[1-9]\d{16}([\dXx])$/, invalidCnid)
      .required(requiredCnid);
  },
  chineseNameValidation: () => {
    const [invalidChineseName, requiredFullName] = getTranslations([
      ErrorMessage.invalidChineseName,
      ErrorMessage.requiredFullName,
    ]);
    return Yup.string()
      .matches(CHINESE_NAME_PATTERN, invalidChineseName)
      .required(requiredFullName);
  },
  fullNameValidation: () => {
    const [invalidFullName, incompleteFullName, requiredFullName] = getTranslations([
      ErrorMessage.invalidFullName,
      ErrorMessage.incompleteFullName,
      ErrorMessage.requiredFullName,
    ]);
    return Yup.string()
      .matches(/^[^\d!#$%&*+;=@\\^_]+$/, invalidFullName)
      .matches(/\S\s\S/, incompleteFullName)
      .required(requiredFullName);
  },
  usernameValidation: () => {
    const [shortUsername, invalidUsername] = getTranslations([
      ErrorMessage.shortUsername,
      ErrorMessage.invalidUsername,
    ]);
    return Yup.string().min(4, shortUsername).matches(/^\w*$/, invalidUsername);
  },
  passwordValidation: () => {
    const requiredPassword = getTranslations(ErrorMessage.requiredPassword);
    return Yup.string().required(requiredPassword);
  },
  passwordConfirmationValidation: () => {
    return Yup.string().oneOf(
      [Yup.ref('password'), null],
      t(`${TP}.passwordNotMatched`, 'Passwords must match'),
    );
  },
  usPhoneValidation: () => {
    const invalidUsPhone = getTranslations(ErrorMessage.invalidUsPhone);
    return Yup.string().length(10, invalidUsPhone);
  },
  phoneValidation: () => {
    const invalidPhone = getTranslations(ErrorMessage.invalidPhone);
    return Yup.string().min(1, invalidPhone).max(50, invalidPhone);
  },
  otpCodeValidation: () => {
    const [requiredOTPCode, invalidOTPCode] = getTranslations([
      ErrorMessage.requiredOTPCode,
      ErrorMessage.invalidOTPCode,
    ]);
    return Yup.string()
      .required(requiredOTPCode)
      .min(6, '')
      .max(6, '')
      .matches(/^\d+$/, invalidOTPCode);
  },
  giftCardCodeValidation: () => {
    return Yup.string()
      .length(19, t(`${TP}.giftCardCodeLengthValidation`, 'Gift Card Code must be 16 characters'))
      .matches(
        /^[\da-z-]+$/i,
        t(`${TP}.giftCardCodeFormatMatches`, 'Gift Card Code must only contain A-Z and 0-9'),
      )
      .required(t(`${TP}.giftCardCodeRequired`, 'Gift Card Code is a required field'));
  },
  giftCardValidationSchema: () => {
    return Yup.object().shape({
      giftCardCode: sharedValidationsMap.giftCardCodeValidation(),
      giftCardValue: Yup.number().nullable(),
    });
  },
};

interface IAddressValidationOption {
  countryIsoCode?: string;
  isAddressRequired?: boolean;
  isChinaCitizenIdCard?: boolean;
  isGuestCheckout?: boolean;
  isPostalCodeRequired?: boolean;
  isStateRequired?: boolean;
}

export const getAddressValidationFields = ({
  countryIsoCode,
  isChinaCitizenIdCard,
  isGuestCheckout = false,
  isPostalCodeRequired = false,
  isStateRequired = false,
}: IAddressValidationOption) => {
  const [
    requiredAddress,
    requiredCity,
    requiredPostalCode,
    requiredCountry,
    requiredState,
    requiredPhone,
  ] = getTranslations([
    ErrorMessage.requiredAddress,
    ErrorMessage.requiredCity,
    ErrorMessage.requiredPostalCode,
    ErrorMessage.requiredCountry,
    ErrorMessage.requiredState,
    ErrorMessage.requiredPhone,
  ]);
  const { requiredEmailValidation, fullNameValidation } = sharedValidationsMap;
  let spec = {
    ...(isGuestCheckout && { email: requiredEmailValidation() }),
    name: fullNameValidation(),
    address1: Yup.string().required(requiredAddress),
    address2: Yup.string().nullable(),
    city: Yup.string().required(requiredCity),
    postalCode: isPostalCodeRequired
      ? Yup.string().required(requiredPostalCode)
      : Yup.string().nullable(),
    countryCode: Yup.string().required(requiredCountry),
    state: isStateRequired ? Yup.string().required(requiredState) : Yup.string().nullable(),
    phone: Yup.string().required(requiredPhone),
    customerIdNumber: Yup.string().nullable(),
    district: Yup.string().nullable(),
  };

  switch (countryIsoCode) {
    case COUNTRY_ISO_CODES.MAINLAND_CHINA:
      spec = {
        ...spec,
        customerIdNumber: isChinaCitizenIdCard
          ? sharedValidationsMap.chineseCnidValidation()
          : Yup.string().nullable(),
        district: Yup.string().required(getTranslations(ErrorMessage.requiredDistrict)),
        name: sharedValidationsMap.chineseNameValidation(),
        phone: Yup.string().nullable(),
        postalCode: Yup.string().nullable(),
      };
      break;
    case COUNTRY_ISO_CODES.AUSTRALIA:
      spec = {
        ...spec,
        state: isStateRequired
          ? Yup.string().required(getTranslations(ErrorMessage.requiredStateAU))
          : Yup.string().nullable(),
      };
      break;
    case COUNTRY_ISO_CODES.CANADA:
      spec = {
        ...spec,
        state: isStateRequired
          ? Yup.string().required(getTranslations(ErrorMessage.requiredStateCA))
          : Yup.string().nullable(),
      };
      break;
    case COUNTRY_ISO_CODES.UNITED_KINGDOM:
      spec = {
        ...spec,
        state: isStateRequired
          ? Yup.string().required(getTranslations(ErrorMessage.requiredStateUK))
          : Yup.string().nullable(),
      };

      break;
    case COUNTRY_ISO_CODES.UNITED_STATES:
      spec = {
        ...spec,
        state: isStateRequired
          ? Yup.string().required(getTranslations(ErrorMessage.requiredStateUS))
          : Yup.string().nullable(),
      };
      break;
    default:
      return spec;
  }

  return spec;
};

export const getShippingAddressValidationSchema = (options: IAddressValidationOption) =>
  Yup.object(getAddressValidationFields(options));

export const getBillingAddressValidationSchema = (options: IAddressValidationOption) => {
  if (options.isAddressRequired) {
    return Yup.object(getAddressValidationFields(options));
  }

  return Yup.object({ name: sharedValidationsMap.fullNameValidation() });
};
